import React, { useState, useEffect } from "react";
import { easeOut, motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import "./Navigation.css";

import HotBlobLogo from "./HotBlobLogo";
import ContactButton from "./ContactButton";
import { BOOK_CALL_URLS, SOCIAL_URLS } from "../constants";

const Navigation: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show button when scrolled past the initial hero view
      if (window.scrollY > 400) {
        setShowButton(true);
      } else {
        setShowButton(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    const url = new URL(window.location.href);
    url.searchParams.set("lang", lang);
    window.history.replaceState({}, "", url.toString());
  };

  const currentLang = i18n.language;
  const lang = currentLang.startsWith("pl") ? "pl" : "en";

  const navbarVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: easeOut },
    },
  };

  return (
    <motion.nav
      className="navbar"
      initial="hidden"
      animate="visible"
      variants={navbarVariants}
    >
      <motion.div variants={itemVariants}>
        <HotBlobLogo to="/" className="logoImg" />
      </motion.div>

      <motion.div className="nav-right" variants={itemVariants} layout>
        <AnimatePresence mode="popLayout">
          <motion.div
            layout
            className="nav-contact-btn-wrapper"
            initial={false}
            animate={
              showButton
                ? { opacity: 1, scale: 1, pointerEvents: "auto" }
                : { opacity: 0, scale: 0.8, pointerEvents: "none" }
            }
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <ContactButton size="small" href={BOOK_CALL_URLS[lang]}>
              {t("navigation.bookCall")}
            </ContactButton>
          </motion.div>
        </AnimatePresence>

        <motion.div className="social-links" layout>
          <a
            href={SOCIAL_URLS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
            aria-label="Instagram"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
            </svg>
          </a>
          <a
            href={SOCIAL_URLS.tiktok}
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
            aria-label="TikTok"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"></path>
            </svg>
          </a>
        </motion.div>

        <motion.div className="lang-switch" layout>
          <button
            onClick={() => changeLanguage("pl")}
            className={`lang-btn ${currentLang === "pl" ? "active" : ""}`}
          >
            PL
          </button>
          <span className="lang-separator">/</span>
          <button
            onClick={() => changeLanguage("en")}
            className={`lang-btn ${currentLang === "en" ? "active" : ""}`}
          >
            EN
          </button>
        </motion.div>
      </motion.div>
    </motion.nav>
  );
};

export default Navigation;
